import { useEffect, useState } from 'react';
import { callPlugin } from '../api/client';
import { disablePush, enablePush, pushState, type PushState } from '../mail/push';
import { isIOS, isStandalone, unblockInstructions } from '../mail/device';
import { Button, Card, CardBody, Input, Switch } from '../ui';

interface Props {
	email: string;
	onBack: () => void;
	onSignOut: () => void;
	/** VAPID public key and worker URL, both supplied by the keystone-push plugin. */
	pushKey?: string;
	workerUrl?: string;
}

interface SignatureFields {
	name: string;
	role: string;
	phone: string;
	// The API client takes a param bag; without this the three fields cannot be
	// passed straight through as one.
	[key: string]: string;
}

/**
 * Three settings, not twenty.
 *
 * SnappyMail exposes folder management, sieve filters, identities, PGP keyrings,
 * themes and contacts sync. None of that is built here on purpose -- the
 * information architecture of that settings area is most of why the old UI read
 * as a technical tool rather than a mail app.
 */
export function SettingsScreen({ email, onBack, onSignOut, pushKey, workerUrl }: Props) {
	return (
		<div className="pane">
			<header className="pane__header">
				<Button variant="ghost" size="sm" onClick={onBack}>← Back</Button>
				<h2 className="pane__title">Settings</h2>
			</header>

			<div className="settings">
				<SignatureSection />
				<NotificationsSection pushKey={pushKey} workerUrl={workerUrl} />
				<ShortcutsSection />

				<Card>
					<CardBody>
						<h2 className="settings__heading">Account</h2>
						<p className="ks-muted" style={{ margin: 0 }}>{email}</p>
						<Button variant="outline" style={{ justifySelf: 'start' }} onClick={onSignOut}>
							Sign out
						</Button>
					</CardBody>
				</Card>
			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ signature */

function SignatureSection() {
	const [fields, setFields] = useState<SignatureFields>({ name: '', role: '', phone: '' });
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		callPlugin<SignatureFields>('KeystoneSignatureGet')
			.then(result =>
				setFields({
					name: result.name ?? '',
					role: result.role ?? '',
					phone: result.phone ?? ''
				})
			)
			.catch(() => setError('Could not load your signature.'))
			.finally(() => setLoading(false));
	}, []);

	async function save() {
		setSaving(true);
		setSaved(false);
		setError('');
		try {
			// The server builds the signature markup from these three values; the
			// browser never submits HTML. That is what keeps it standardised rather
			// than a template each person can edit.
			await callPlugin<unknown>('KeystoneSignatureSave', fields);
			setSaved(true);
		} catch {
			setError('Could not save your signature.');
		} finally {
			setSaving(false);
		}
	}

	const update = (key: keyof SignatureFields) => (event: { target: { value: string } }) => {
		setSaved(false);
		setFields(previous => ({ ...previous, [key]: event.target.value }));
	};

	return (
		<Card>
			<CardBody>
			<h2 className="settings__heading">Signature</h2>
			<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
				Added to the bottom of messages you send. Branding is fixed.
			</p>

			<Input
				label="Full name"
				value={fields.name}
				onChange={update('name')}
				disabled={loading}
			/>
			<Input label="Role" value={fields.role} onChange={update('role')} disabled={loading} />
			<Input
				label="Phone"
				description="Optional"
				value={fields.phone}
				onChange={update('phone')}
				disabled={loading}
			/>

			{error && <p className="ks-error" style={{ margin: 0 }}>{error}</p>}

			<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
				<Button onClick={() => void save()} disabled={loading} loading={saving}>
					Save
				</Button>
				{saved && <span className="ks-muted" style={{ fontSize: 13 }}>Saved.</span>}
			</div>
			</CardBody>
		</Card>
	);
}

/* -------------------------------------------------------------- notifications */

function NotificationsSection({ pushKey, workerUrl }: { pushKey?: string; workerUrl?: string }) {
	const [state, setState] = useState<PushState | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		pushState()
			.then(setState)
			.catch(() => setState('unsupported'));
	}, []);

	async function toggle() {
		if (!pushKey || !workerUrl) {
			return;
		}
		setBusy(true);
		setError('');
		try {
			setState('on' === state ? await disablePush() : await enablePush(pushKey, workerUrl));
		} catch (e: unknown) {
			setError(e instanceof Error ? e.message : 'Could not change your notification setting.');
		} finally {
			setBusy(false);
		}
	}

	const configured = Boolean(pushKey && workerUrl);

	return (
		<Card>
			<CardBody>
			<h2 className="settings__heading">Notifications</h2>

			{!configured && (
				<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
					Notifications are not set up on this server.
				</p>
			)}

			{configured && 'unsupported' === state && (
				<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
					{isIOS && !isStandalone()
						? 'On iPhone, notifications only work once Keystone Mail is on your Home Screen: tap the Share button, choose “Add to Home Screen”, then open it from there and come back to this page.'
						: 'This browser cannot show notifications. Try Chrome, Edge, Firefox or Safari.'}
				</p>
			)}

			{configured && 'blocked' === state && (
				<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
					{unblockInstructions()}
				</p>
			)}

			{configured && ('on' === state || 'off' === state) && (
				<>
					<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
						{'on' === state
							? 'This device will be notified when new mail arrives, even with the app closed.'
							: 'Get notified when new mail arrives, even with the app closed.'}
					</p>
					<Switch checked={'on' === state} disabled={busy} onChange={() => void toggle()}>
						{'on' === state ? 'On for this device' : 'Off'}
					</Switch>
				</>
			)}

			{error && <p className="ks-error" style={{ margin: 0 }}>{error}</p>}
			</CardBody>
		</Card>
	);
}

/* ------------------------------------------------------------------ shortcuts */

const SHORTCUTS: [string, string][] = [
	['c', 'New message'],
	['/', 'Search'],
	['j / k', 'Next / previous message'],
	['e', 'Archive'],
	['#', 'Delete'],
	['s', 'Star or unstar'],
	['r', 'Reply'],
	['a', 'Reply all'],
	['f', 'Forward'],
	['u or Esc', 'Back to the list']
];

/** Same keys as Gmail, so anyone who already knows them needs to learn nothing. */
function ShortcutsSection() {
	return (
		<Card>
			<CardBody>
				<h2 className="settings__heading">Keyboard shortcuts</h2>
				<p className="ks-muted" style={{ margin: 0, fontSize: 13 }}>
					On a computer. The same keys Gmail uses.
				</p>
				<dl className="shortcuts">
					{SHORTCUTS.map(([keys, action]) => (
						<div key={keys} className="shortcuts__row">
							<dt><kbd>{keys}</kbd></dt>
							<dd>{action}</dd>
						</div>
					))}
				</dl>
			</CardBody>
		</Card>
	);
}
